/**
 * Status Indicator - Button component for system status and WebRTC controls
 */

let statusButton = null;
let webrtcClickHandler = null;

const STATE_CLASSES = [
  'notification-connecting',
  'notification-online',
  'notification-offline',
  'notification-disconnected',
  'initial',
  'muted',
  'unmuted'
];

function updateButtonClass(newClass) {
  if (!statusButton) return;
  statusButton.classList.remove(...STATE_CLASSES);
  statusButton.classList.add(newClass);
}

function animateButtonWidth(button, newText) {
  if (!button) return;

  const label = button.querySelector('span');
  const currentText = label ? label.textContent : button.textContent;

  // Skip if text unchanged
  if (currentText === newText) return;

  // Get transition timing from CSS
  const timing = getComputedStyle(button).transitionDuration;
  const curve = getComputedStyle(button).transitionTimingFunction;

  // Keep all transitions except width while measuring
  button.style.transition = `border-radius ${timing} ${curve}, opacity ${timing} ${curve}, color ${timing} ${curve}, background-color ${timing} ${curve}`;

  button.style.width = 'auto';
  const currentWidth = button.offsetWidth;

  // Update text and measure new width
  if (label) {
    label.textContent = newText;
  } else {
    button.textContent = newText;
  }

  button.style.width = 'auto';
  const newWidth = button.offsetWidth;

  // Restore all transitions including width
  button.style.width = `${currentWidth}px`;
  button.offsetHeight; // Force reflow
  button.style.removeProperty('transition');

  requestAnimationFrame(() => {
    button.style.width = `${newWidth}px`;
  });
}

export function createStatusButton() {
  const controlsContainer = document.getElementById('controls');
  if (!controlsContainer) return;

  statusButton = document.createElement('button');
  statusButton.id = 'audio-control';
  statusButton.appendChild(document.createElement('span'));

  statusButton.addEventListener('click', () => {
    const isWebRTCState = statusButton.classList.contains('initial') ||
                         statusButton.classList.contains('muted') ||
                         statusButton.classList.contains('unmuted');

    if (isWebRTCState && webrtcClickHandler) {
      webrtcClickHandler();
    }
  });

  controlsContainer.appendChild(statusButton);
  return statusButton;
}

export function showNotification(type, customText = null) {
  if (!statusButton) return;

  const notificationTexts = {
    connecting: 'connecting...',
    online: 'maschin online',
    offline: 'maschin offline',
    disconnected: 'disconnected'
  };

  const text = customText || notificationTexts[type] || type;

  updateButtonClass(`notification-${type}`);
  animateButtonWidth(statusButton, text);
}

export function showWebRTCState(state, text) {
  if (!statusButton) return;

  updateButtonClass(state);
  animateButtonWidth(statusButton, text);
}

export function updateButtonOnlineStatus(isOnline) {
  if (!statusButton) return;

  // Update status circle
  const connectionStatus = document.getElementById('connection-status');
  if (connectionStatus) {
    connectionStatus.classList.remove('hidden');
    connectionStatus.classList.toggle('connected', isOnline);
    connectionStatus.classList.toggle('disconnected', !isOnline);
  }

  // Update button
  showNotification(isOnline ? 'online' : 'offline');
}

export function registerWebRTCClickHandler(handler) {
  webrtcClickHandler = handler;
}

export async function initStatusFromBackend() {
  try {
    const response = await fetch('/api/status');
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    updateButtonOnlineStatus(data.online);
    return data.online;
  } catch (err) {
    console.error('[🔘 Status Indicator] Failed to fetch status:', err);
    updateButtonOnlineStatus(false);
    return false;
  }
}
